import { ApplicationConfig, provideBrowserGlobalErrorListeners, isDevMode } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withFetch } from '@angular/common/http';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideTranslateService } from '@ngx-translate/core';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';
import { providePrimeNG } from 'primeng/config';
import { MessageService, ConfirmationService } from 'primeng/api';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAnalytics, provideAnalytics } from '@angular/fire/analytics';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';

import { routes } from './app.routes';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideServiceWorker } from '@angular/service-worker';

/**
 * Preset PrimeNG aligné sur les custom properties de src/styles.scss:
 * accent emerald, neutres froids, clair + sombre.
 */
const EquiFootTheme = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '6px',
      sm: '8px',
      md: '10px',
      lg: '12px',
      xl: '16px'
    },
    emerald: {
      50: '#ecfdf5',
      100: '#d1fae5',
      200: '#a7f3d0',
      300: '#6ee7b7',
      400: '#34d399',
      500: '#10b981',
      600: '#059669',
      700: '#047857',
      800: '#065f46',
      900: '#064e3b',
      950: '#022c22'
    }
  },
  semantic: {
    transitionDuration: '0.15s',
    focusRing: {
      width: '2px',
      style: 'solid',
      color: '{primary.color}',
      offset: '2px',
      shadow: 'none'
    },
    primary: {
      50: '{emerald.50}',
      100: '{emerald.100}',
      200: '{emerald.200}',
      300: '{emerald.300}',
      400: '{emerald.400}',
      500: '{emerald.500}',
      600: '{emerald.600}',
      700: '{emerald.700}',
      800: '{emerald.800}',
      900: '{emerald.900}',
      950: '{emerald.950}'
    },
    formField: {
      borderRadius: '{border.radius.sm}',
      paddingX: '0.875rem',
      paddingY: '0.6875rem'
    },
    colorScheme: {
      light: {
        // primary.700 pour garder du texte blanc >= 4.5:1 sur bouton plein.
        primary: {
          color: '{primary.700}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.800}',
          activeColor: '{primary.900}'
        },
        surface: {
          0: '#ffffff',
          50: '#f7f9fa',
          100: '#eef2f5',
          200: '#e1e7ec',
          300: '#c3cdd6',
          400: '#9aa6b2',
          500: '#7c8794',
          600: '#56626f',
          700: '#3b4653',
          800: '#26313c',
          900: '#101a24',
          950: '#080e14'
        }
      },
      dark: {
        primary: {
          color: '{primary.400}',
          contrastColor: '#04231a',
          hoverColor: '{primary.300}',
          activeColor: '{primary.200}'
        },
        surface: {
          0: '#131a21',
          50: '#182028',
          100: '#1e2831',
          200: '#263039',
          300: '#3a4753',
          400: '#5b6975',
          500: '#76838f',
          600: '#9fadb9',
          700: '#c3ced7',
          800: '#dde5ea',
          900: '#e9eff3',
          950: '#f5f8fa'
        }
      }
    }
  }
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideAnimationsAsync(),
    MessageService,
    ConfirmationService,
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })
    ),
    provideHttpClient(withFetch()),
    provideTranslateService({
      defaultLanguage: 'fr'
    }),
    provideTranslateHttpLoader({
      prefix: './i18n/',
      suffix: '.json'
    }),
    providePrimeNG({
      theme: {
        preset: EquiFootTheme,
        options: {
          // Le thème est posé sur <html data-theme> par le script inline d'index.html.
          darkModeSelector: '[data-theme="dark"]',
          cssLayer: {
            name: 'primeng',
            order: 'tailwind-base, primeng, tailwind-utilities'
          }
        }
      }
    }),
    provideClientHydration(withEventReplay()),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000'
    }),
    provideFirebaseApp(() =>
      initializeApp({
        apiKey: 'AIzaSyDyb-sQc-KVB1sugdnLZWke1GGZHGBQGv4',
        authDomain: 'equi-foot-front.firebaseapp.com',
        projectId: 'equi-foot-front',
        storageBucket: 'equi-foot-front.firebasestorage.app',
        messagingSenderId: '645831376322',
        appId: '1:645831376322:web:567dfd5c49ea5fa67cea5b',
        measurementId: 'G-9C3PTG27EK'
      })
    ),
    provideAnalytics(() => getAnalytics()),
    provideFirestore(() => getFirestore())
  ]
};
